import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, protectedResources } from './authConfig';
import { ServiceNowAPI, SalesforceAPI } from './services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const { instance, inProgress, accounts } = useMsal();
    const [user, setUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const getAccessToken = useCallback(async (apiName, retryCount = 0) => {
        if (!accounts[0]) {
            console.error('No account available when requesting token');
            throw new Error('No account available');
        }

        if (!protectedResources[apiName]) {
            console.error(`Invalid API name: ${apiName}`);
            throw new Error('Invalid API name');
        }

        const request = {
            scopes: protectedResources[apiName].scopes,
            account: accounts[0]
        };
        
        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            console.error(`Silent token acquisition failed for ${apiName}:`, error);
            
            // If we haven't exceeded retry attempts and it's not a user interaction required error
            if (retryCount < 3 && error.name !== "BrowserAuthError") {
                console.log(`Retrying token acquisition for ${apiName}, attempt ${retryCount + 1}`);
                await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
                return getAccessToken(apiName, retryCount + 1);
            }
            
            // If silent refresh fails and we're out of retries, try interactive
            try {
                const response = await instance.acquireTokenRedirect(request);
                return response.accessToken;
            } catch (redirectError) {
                console.error(`Interactive token acquisition failed for ${apiName}:`, redirectError);
                throw redirectError;
            }
        }
    }, [accounts, instance]);

    useEffect(() => {
        let isMounted = true;

        const initializeAuth = async () => {
            if (accounts.length > 0 && !isInitialized) {
                const account = accounts[0];
                if (isMounted) setUser(account);
                
                try {
                    console.log('Getting access tokens for APIs...');
                    
                    // Get and set tokens in parallel
                    const [servicenowToken, salesforceToken] = await Promise.all([
                        getAccessToken('servicenow'),
                        getAccessToken('salesforce')
                    ]);
                    
                    ServiceNowAPI.setToken(servicenowToken);
                    SalesforceAPI.setToken(salesforceToken);
                    
                    if (isMounted) setIsInitialized(true);
                    
                } catch (error) {
                    console.error('Failed to get or set access tokens:', error);
                    // Don't set isInitialized to false here to prevent infinite retries
                }
            } else if (!inProgress && !isInitialized) {
                console.log('No account found, initiating login...');
                try {
                    await instance.loginRedirect(loginRequest);
                } catch (error) {
                    console.error('Login failed:', error);
                }
            }
        };

        initializeAuth();

        return () => {
            isMounted = false;
        };
    }, [accounts, inProgress, instance, getAccessToken, isInitialized]);

    const login = async () => {
        try {
            await instance.loginRedirect(loginRequest);
        } catch (error) {
            console.error('Login failed:', error);
        }
    };

    const logout = async () => {
        try {
            await instance.logoutRedirect();
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            logout, 
            getAccessToken, 
            isAuthenticated: accounts.length > 0 && isInitialized, 
            inProgress 
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
